import { configureStore, createListenerMiddleware } from "@reduxjs/toolkit";
import { createLogger } from "redux-logger";
import { Middleware, Store } from 'redux';
import rootReducer from "../reducers/rootReducer";
import { HubConnectionBuilder, HttpTransportType, HubConnectionState } from "@microsoft/signalr";
import configuration from "../config";
import { useDispatch, useSelector as reduxUseSelector, TypedUseSelectorHook } from "react-redux";
import { psoLogsActions } from "./psoLogs";
import { layoutActions } from "./layout";
import { getAccessToken } from "../auth/msal";

const connection = new HubConnectionBuilder()
    .withUrl(`${configuration.API_SERVER}/reportingHub`, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => getAccessToken(),
    })
    .build();

connection.serverTimeoutInMilliseconds = 120000;

const signalRInvokeMiddleware: Middleware = storeAPI => next => async action => {
    if (connection.state === HubConnectionState.Disconnected) {
        await connection.start();
        await connection.invoke("Subscribe");
    }

    return next(action);
};

function signalRRegisterCommands(store: Store) {
    connection.on("Notify", (status) => {
        store.dispatch(layoutActions.hubDashboardLoadProgressUpdated({ statuses: [status] }));
    });

    connection.on("NotifyJobs", (statusGroup) => {
        store.dispatch(layoutActions.hubNotificationJobs(statusGroup));
    });

    connection.on("NotifyQueryExecution", (status) => {
        store.dispatch(psoLogsActions.setEventStatus(status));
    });
}

const middleware = [
    signalRInvokeMiddleware,
];
// Create the middleware instance and methods
const listenerMiddleware = createListenerMiddleware();

if (process.env.NODE_ENV !== "production") {
    middleware.push(createLogger({ collapsed: true }));
}

const store = configureStore({
    reducer: rootReducer(),
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            thunk: true,
            serializableCheck: {
                isSerializable: () => true,
            },
            immutableCheck: true,
        })
            .prepend(listenerMiddleware.middleware)
            .concat(middleware),
    devTools: process.env.NODE_ENV !== "production",
});

signalRRegisterCommands(store);

export { store, connection };
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useSelector: TypedUseSelectorHook<RootState> = reduxUseSelector;